import React, { useState } from "react";
import { RoleResponse } from "../../../app/models/responseModels/roleResponse";
import { UpdateRoleRequest } from "../../../app/models/requestModels/roleRequest";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import { useSelector } from "react-redux";
import { PAGES } from "../../../enums/PagesEnum";
import { toast } from "react-toastify";
import { updateRole } from "../../../app/features/role/roleSlice";
import getAxiosError from "../../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../../app/features/login/loginSlice";
import SubmitModalWithEditAndDelete from "../../../components/common/modalPopup/SubmitModalWithEditAndDelete";
import { Box, Grid, MenuItem, TextField } from "@mui/material";

interface EditRoleModalProps {
  closeEditRoleModal: CallableFunction;
  onCompleted: CallableFunction;
  roleRowData: RoleResponse;
}

function UpdateRoleModel(
  id: string,
  name: string,
  deleted: string
): UpdateRoleRequest {
  return {
    id: id,
    name: name,
    deleted: deleted,
  };
}

const EditRoleModal = ({
  closeEditRoleModal,
  onCompleted,
  roleRowData,
}: EditRoleModalProps) => {
  const dispatch = useAppDispatch();

  // ***** Get page Permissions *****
  const pagePermission = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.pagePermissions.find(
      (x) => x.pageName === PAGES.ROLE
    )
  );

  const IsUpdatePermisssion = pagePermission
    ? pagePermission.isUpdatePermission
    : false;

  const IsAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings
      ? state.login?.authResponse?.userSettings?.roles.includes(
          "Administrators"
        )
      : false
  );

  // ***** States *****
  const [showSubmitButton, setShowSubmitButton] = useState(false);
  const [name, setName] = useState(roleRowData.name);
  const [deleted, setDeleted] = useState(
    roleRowData.deleted ? "true" : "false"
  );

  // ***** Handlers *****
  const handleSubmit = async () => {
    try {
      if (name.length === 0) {
        toast.error("Role name is required");
        return false;
      }
      const model = UpdateRoleModel(roleRowData.id, name, deleted);
      const response = await dispatch(updateRole(model)).unwrap();
      if (response.isSuccess) {
        toast.success(response.message!, {
          toastId: "updateRoleSuccess",
        });
        onCompleted();
      } else {
        toast.error(response.message!, {
          toastId: "updateRoleError",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "updateRoleException",
      });
    }
    return true;
  };

  return (
    <>
      <SubmitModalWithEditAndDelete
        title="Edit Role"
        updateAsyncOperation={handleSubmit}
        closeSubmitModalWithEditAndDelete={closeEditRoleModal}
        size="sm"
        isLoading={false}
        submitButtonName="Save"
        closeModalAfterSubmit={true}
        confirmMessage="Are you sure you want to delete?"
        isUpdatePermission={IsUpdatePermisssion && IsAdmin}
        isDeletePermission={false}
        setShowSubmitButton={setShowSubmitButton}
        showSubmitButton={showSubmitButton}
        showDeleteButton={false}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Grid container rowSpacing={2} columnSpacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <TextField
                margin="normal"
                id="name"
                label="Name"
                name="name"
                size="small"
                fullWidth
                required
                disabled={!showSubmitButton}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </Grid>
          </Grid>
          <Grid container rowSpacing={2} columnSpacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <TextField
                select
                margin="normal"
                id="deleted"
                label="Deleted"
                name="deleted"
                size="small"
                fullWidth
                disabled={!showSubmitButton}
                value={deleted}
                onChange={(event) => setDeleted(event.target.value)}
              >
                <MenuItem key="true" value="true">
                  True
                </MenuItem>
                <MenuItem key="false" value="false">
                  False
                </MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Box>
      </SubmitModalWithEditAndDelete>
    </>
  );
};

export default EditRoleModal;
